import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import * as api from '@/api/endpoints'
import { queryKeys } from '@/api/query-keys'
import { env } from '@/env'
import { accountEventsUrl, openAccountEvents } from '@/lib/account-events'
import { notify, notifySuccess } from '@/lib/notify'
import { platformName } from '@/lib/text'
import {
  type AccountNotification,
  type GiveawayEndedNotification,
  type GiveawayEntryNotification,
  type GiveawayWonNotification,
  describeEnded,
  describeWin,
  giveawayPathOf,
  parseAccountNotification,
  summarizeEntries,
  WIN_CATCH_UP_MS,
} from '@/models/account-notification'
import { useAuthStore } from '@/store/auth'
import { useConnectStore } from '@/store/connect'
import { useMe } from './queries'

const lastSeenKey = (userId: number) => `jlm:account-notification-seen:${userId}`

function readLastSeen(userId: number): number | null {
  try {
    const raw = localStorage.getItem(lastSeenKey(userId))
    const value = raw === null ? NaN : Number(raw)
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

function writeLastSeen(userId: number, id: number) {
  try {
    localStorage.setItem(lastSeenKey(userId), String(id))
  } catch {
    // Storage unavailable: the worst case is hearing about an entry twice.
  }
}

/**
 * Runs `fn` while holding a lock shared by every tab, where the browser has
 * one. Every open tab gets the same event; this is how only one announces it.
 */
function withTabLock(fn: () => void | Promise<void>): Promise<void> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) return Promise.resolve().then(fn)
  return locks
    .request('jlm:account-notifications', async () => {
      await fn()
    })
    .then(() => undefined)
}

/**
 * Keeps the account events socket open while signed in, and acts on what
 * arrives:
 *
 * - platforms_changed: refresh connections. The tab that started the connect
 *   (in a new tab) also says it worked.
 * - notification: someone entered one of your giveaways, one of them ended,
 *   or you won one.
 *   Shown once across all your tabs, and caught up on (from the API) after
 *   being offline.
 *
 * Not on /auth/* callback pages: the tab doing the linking has said so itself.
 */
export function useAccountEvents() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const token = useAuthStore((state) => state.token)
  const { data: me } = useMe()
  const userId = me?.id ?? null
  const enabled = token !== null && userId !== null && !pathname.startsWith('/auth/')

  // Latest navigate without reconnecting when it changes.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    if (!enabled || token === null || userId === null) return

    const announce = (items: AccountNotification[]) => {
      const entries = items.filter(
        (item): item is GiveawayEntryNotification => item.kind === 'giveaway_entry',
      )
      const wins = items.filter(
        (item): item is GiveawayWonNotification => item.kind === 'giveaway_won',
      )

      for (const win of wins) {
        const { message, action } = describeWin(win)
        notify({
          message,
          color: 'positive',
          duration: 15_000,
          action: { label: action.label, onClick: () => navigateRef.current(action.path) },
        })
      }
      if (wins.length > 0) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.myLoot })
      }

      const ended = items.filter(
        (item): item is GiveawayEndedNotification => item.kind === 'giveaway_ended',
      )
      for (const item of ended) {
        const path = giveawayPathOf(item)
        notify({
          message: describeEnded(item),
          color: 'info',
          duration: 12_000,
          action: { label: 'See winners', onClick: () => navigateRef.current(path) },
        })
      }

      for (const summary of summarizeEntries(entries)) {
        notify({
          message: summary.message,
          color: 'info',
          duration: 8_000,
          action: { label: 'View', onClick: () => navigateRef.current(summary.path) },
        })
      }
      // Entry counts, the owner's entrants list and ended giveaways are now stale.
      const ids = new Set(items.map((item) => item.giveawayId))
      for (const id of ids) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.giveawayEntrants(id) })
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGiveaways })
      void queryClient.invalidateQueries({ queryKey: ['giveaway'] })
    }

    /** Announces the ones no tab has yet, and marks them seen. */
    const take = (items: AccountNotification[]) =>
      withTabLock(() => {
        const seen = readLastSeen(userId) ?? 0
        const fresh = items.filter((item) => item.id > seen)
        if (fresh.length === 0) return
        writeLastSeen(userId, Math.max(...fresh.map((item) => item.id)))
        announce(fresh)
      })

    const catchUp = () =>
      withTabLock(async () => {
        const seen = readLastSeen(userId)
        const items = await api.getMyNotifications(seen ?? 0)
        if (seen === null) {
          // First time on this device: start from now rather than announcing
          // everything that has ever happened -- except recent wins, which a
          // winner shouldn't miss just because they're on a new device.
          writeLastSeen(userId, items.length ? items[items.length - 1].id : 0)
          const since = Date.now() - WIN_CATCH_UP_MS
          const wins = items.filter(
            (item) => item.kind === 'giveaway_won' && item.at.getTime() >= since,
          )
          if (wins.length > 0) announce(wins)
          return
        }
        const fresh = items.filter((item) => item.id > seen)
        if (fresh.length === 0) return
        writeLastSeen(userId, fresh[fresh.length - 1].id)
        announce(fresh)
      }).catch(() => undefined) // Not worth a toast; the next connect retries.

    return openAccountEvents({
      url: accountEventsUrl(env.apiBaseUrl, window.location.href),
      token,
      onReady: (reconnected) => {
        void catchUp()
        // A connection made while the socket was down would otherwise be missed.
        if (reconnected) void queryClient.invalidateQueries({ queryKey: queryKeys.myPlatforms })
      },
      onEvent: (event) => {
        if (event.type === 'platforms_changed') {
          void queryClient.invalidateQueries({ queryKey: queryKeys.myPlatforms })
          // The servers you manage come from the linked Discord account.
          void queryClient.invalidateQueries({ queryKey: queryKeys.managedGuilds })
          if (useConnectStore.getState().finish(event.platform)) {
            notifySuccess(`${platformName(event.platform)} connected`)
          }
          return
        }
        const notification = parseAccountNotification(event.notification)
        if (notification) void take([notification])
      },
    })
  }, [enabled, token, userId, queryClient])
}
